import { useCallback, useEffect, useRef } from 'react';
import { CURRENT_RELEASE } from '@/config/releaseNotes';

interface AppUpdateHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 프로필에서 다시 확인하는 업데이트 내역입니다.
 * 로그인 직후 노출되는 UpdateNoticeModal과 같은 CURRENT_RELEASE를 사용하므로
 * 다음 배포 때 한 곳만 바꾸면 두 화면의 내용이 항상 일치합니다.
 */
export default function AppUpdateHistoryModal({ isOpen, onClose }: AppUpdateHistoryModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      const focusTimer = window.setTimeout(() => modalRef.current?.focus(), 50);
      return () => {
        window.clearTimeout(focusTimer);
        document.body.style.overflow = '';
        previousFocusRef.current?.focus();
      };
    }

    document.body.style.overflow = '';
    return undefined;
  }, [isOpen]);

  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-history-title"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/45 animate-fade-in" />

      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl bg-background-50 shadow-2xl animate-slide-up sm:max-w-md sm:rounded-3xl sm:animate-scale-in"
      >
        <div className="flex justify-center pb-1 pt-3 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-foreground-200" />
        </div>

        <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-4 sm:pt-6">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-700">
              <i className="ri-history-line" />
              앱 업데이트 내역
            </div>
            <h2 id="app-update-history-title" className="mt-3 text-xl font-heading font-bold text-foreground-950">
              {CURRENT_RELEASE.title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-foreground-500">
              {CURRENT_RELEASE.description}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-secondary-100"
            aria-label="업데이트 내역 닫기"
          >
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>

        <div className="mx-6 rounded-2xl border border-accent-100 bg-accent-50/60 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-foreground-900">Family Album v{CURRENT_RELEASE.version}</span>
            <span className="text-xs text-foreground-500">{CURRENT_RELEASE.date}</span>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          {CURRENT_RELEASE.changes.map((change) => (
            <div key={change.title} className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-secondary-100">
                <i className={`${change.icon} text-lg text-accent-600`} />
              </div>
              <div className="pt-0.5">
                <h3 className="text-sm font-semibold text-foreground-900">{change.title}</h3>
                <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">{change.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-accent-500 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-600"
          >
            확인했어요
          </button>
          <p className="mt-3 text-center text-[11px] text-foreground-400">
            다음 버전이 배포되면 이 화면도 새 내용으로 자동 갱신됩니다.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-scale-in { animation: scale-in 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
      `}</style>
    </div>
  );
}
